// @flow
import React, { Component, PropTypes } from 'react';
import type { RequestStates } from 'types/redux';
import type { Errors } from 'types/errors';
import type { Cohort, Latitude, Longitude } from 'types/entities';
import DocumentTitle from 'react-document-title';
import SimulationMap from 'components/map/SimulationMap';
import type { Simulation } from 'types/dashboard';
import Config from 'config';
import {
  cohortGet,
  unsetCohort,
} from 'actions/cohorts';
import _ from 'lodash';
import { connect } from 'react-redux';
import { resetErrorState } from 'actions/helpers';
import { startSimulation } from 'actions/app';
import DisplayForm from './simulation/components/DisplayForm';
import './SimulationPage.scss';
import BookingStatsPanel from './simulation/BookingStatsPanel';
import handleSimulationEvent from './simulation/simulation-events-handler';
import ReportContainer from '../reports/ReportContainer';
import SimulationDetailsPanel from './simulation/SimulationDetailsPanel';

// IT.29TV8TPSSTDV4
const DEFAULT_LATITUDE: number = 1.347597342;
const DEFAULT_LONGITUDE: number = 103.8516846;

type Props = {
  cohortGet: Function,
  cohortGetRequest: RequestStates,
  cohortGetErrors: Errors,

  unsetCohort: Function,
  activeCohortID: string,
  activeCohort: Cohort,

  startSimulation: Function,
  startSimulationRequest: RequestStates,
  startSimulationErrors: Errors,

  simulationGetRequest: RequestStates,
  simulationCompleted: boolean,

  totalBookings: number,
  successfulRides: number,
  allocationRate: number,
  successRate: number,
  driverIDs: Array<string>,
  passengerIDs: Array<string>,
  errors: Errors,
  timeoutEvent: boolean,
  transitionViewport: Function,
  bookings: Object,
  resetErrorState: Function,
  resourceCreationError: boolean,
}

type State = {
  mapCenterLatitude: Latitude,
  mapCenterLongitude: Longitude,
  showReportDialog: boolean,
  showSimulationParams: boolean,
  searchCohortID: string,
  isCohortCreated: boolean,
};

const requestErrors = [
  'cohortGet',
  'startSimulation',
];

// eslint-disable-next-line react/prefer-stateless-function
export class SimulationPage extends Component {
  static contextTypes = {
    store: PropTypes.object,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      showReportDialog: false,
      mapCenterLatitude: DEFAULT_LATITUDE,
      mapCenterLongitude: DEFAULT_LONGITUDE,
      showSimulationParams: false,
      searchCohortID: '',
      isCohortCreated: false,
    };
    this.cohortRequestInFlight = false;
  }

  state: State;

  componentDidMount() {
    this.props.resetErrorState(requestErrors);
    const {
      unsetCohort: deactivateCohort,
      activeCohortID,
    } = this.props;

    deactivateCohort(activeCohortID);
  }

  componentWillReceiveProps(nextProps: Props) {
    const {
      activeCohort,
      activeCohortID,
    } = nextProps;

    if ((!activeCohort && activeCohortID) || (activeCohortID && activeCohort.cohortID !== activeCohortID)) {
      if (!this.cohortRequestInFlight) {
        this.cohortRequestInFlight = true;
        this.props.cohortGet(activeCohortID);
      }
    }

    if (!_.isEmpty(activeCohort) && activeCohort.cohortID) {
      this.cohortRequestInFlight = false;
    }
  }

  componentDidUpdate(prevProps: Props) {
    const {
      simulationGetRequest: prevSimulationGetRequest,
      simulationCompleted: prevSimulationCompleted,
      cohortGetRequest: prevCohortGetRequest,
      resourceCreationError: prevresourceCreationError,
    } = prevProps;

    const {
      simulationGetRequest,
      simulationCompleted,
      unsetCohort: deactivateCohort,
      activeCohortID,
      cohortGetRequest,
      resourceCreationError,
    } = this.props;
    const successfulSimulationRequest = !prevSimulationGetRequest.isSuccessful && simulationGetRequest.isSuccessful;
    const successfulSimulationReport = !prevSimulationCompleted && simulationCompleted;
    const successfulCohortGetRequest: boolean = !prevCohortGetRequest.isSuccessful &&
        cohortGetRequest.isSuccessful;
    const failedSimulationReport = !prevresourceCreationError && resourceCreationError;

    if (successfulSimulationRequest) {
      this.connectActiveCohortSocket();
    }
    if (successfulSimulationReport || failedSimulationReport) {
      deactivateCohort(activeCohortID);
      this.disconnectActiveCohortSocket();
      this.showDialog(true);
    }

    if (successfulCohortGetRequest) {
      this.setCohortCreated(true);
      this.showSimulationParams(true);
    }
  }

  componentWillUnmount() {
    this.disconnectActiveCohortSocket();
  }

  setCohortCreated = (created: boolean) => {
    this.setState({
      isCohortCreated: created,
    });
  };

  setMapCenter = (latitude: Number, longitude: Number) => {
    this.setState({
      mapCenterLatitude: latitude,
      mapCenterLongitude: longitude,
    });
  };

  showDialog = (show: boolean) => {
    this.setState({
      showReportDialog: show,
    });
  };

  showSimulationParams = (show: boolean) => {
    this.setState({
      showSimulationParams: show,
    });
  };

  closeDialog = () => {
    this.showDialog(false);
    this.disconnectActiveCohortSocket();
  };

  SimulationModal = (
    showReport: boolean,
    closeDialog: Function
  ) => {
    let contents = <ReportContainer
      details={this.props}
    />;
    if (!showReport) {
      return null;
    }
    if (this.props.resourceCreationError) {
      contents = (<div className="creation_error">
        An error has occurred while creating resources. Please re-run the simulation.
      </div>);
    }
    return (
      <div className="modal is-active">
        <div className="modal-background" />
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Simulation Results Report</p>
            <button className="delete" aria-label="close" onClick={closeDialog} />
          </header>
          <section className="modal-card-body">
            {contents}
          </section>
          <footer className="modal-card-foot">
            <button className="button" onClick={closeDialog}>Cancel</button>
          </footer>
        </div>
      </div>
    );
  };

  connectActiveCohortSocket() {
    if (this.cohortSocket) {
      this.cohortSocket.close();
    }
    const cohortID: string = this.props.activeCohortID;
    const cohortSocket = new WebSocket(`${Config.WEBSOCKET_BASE_URL}/cohorts/${cohortID}/events`);

    cohortSocket.onmessage = (event: any) => {
      const action = handleSimulationEvent(cohortID, JSON.parse(event.data));
      if (action && !_.isEmpty(action.type)) {
        this.context.store.dispatch(action);
      }
    };
    cohortSocket.onclose = () => {
      if (this.cohortSocket !== cohortSocket) {
        return;
      }

      this.disconnectActiveCohortSocket();
    };
    this.cohortSocket = cohortSocket;
  }

  disconnectActiveCohortSocket() {
    if (this.cohortSocket) {
      this.cohortSocket.onclose = null;
      this.cohortSocket.close();
      this.cohortSocket = null;
    }
  }

  cohortSocket: ?Object;
  cohortRequestInFlight: boolean;

  render() {
    const {
      activeCohort,
    } = this.props;

    let showDisplay = <DisplayForm
      setMapCenter={this.setMapCenter}
      setSimulationParams={this.showSimulationParams}
      disconnectActiveCohortSocket={this.disconnectActiveCohortSocket}
      isCohortCreated={this.state.isCohortCreated}
      setCohortCreated={this.setCohortCreated}
      transitionViewport={this.props.transitionViewport}
      cohortGetRequest={this.props.cohortGetRequest}
      cohortGetErrors={this.props.cohortGetErrors}
      cohortGet={this.props.cohortGet}
      resetErrorState={this.props.resetErrorState}
    />;

    if (this.state.showSimulationParams) {
      showDisplay = <SimulationDetailsPanel
        onStart={(Simulations: Simulation) => {
          this.props.resetErrorState(requestErrors);
          this.props.startSimulation(Simulations);
        }}
        simulationCompleted={this.props.simulationCompleted}
        errors={this.props.startSimulationErrors}
        resetErrorState={this.props.resetErrorState}
        transitionViewport={(latitude: Number, longitude: Number) => {
          this.setMapCenter(latitude, longitude);
        }}
        back={() => {
          this.showSimulationParams(false);
          this.props.unsetCohort(this.props.activeCohortID);
          this.setCohortCreated(false);
          this.setState({
            searchCohortID: '',
          });
          this.props.resetErrorState(requestErrors);
          this.disconnectActiveCohortSocket();
        }}
        activeCohortID={this.props.activeCohortID}
        isCohortCreated={this.state.isCohortCreated}
      />;
    }

    return (
      <DocumentTitle title="Marionette">
        <div className="map-page">
          <div className="map-page-content columns is-gapless">
            <div className="column is-one-quarter map-sidebar-container">
              <div className="map-sidebar">
                {showDisplay}
              </div>
            </div>
            <div className="column">
              <SimulationMap
                accessToken={Config.MAPBOX_TOKEN}
                mapboxStyle={Config.MAP_STYLE}
                cohort={activeCohort}
                mode={'DISPLAY_BOOKING'}
                mapCenterLatitude={this.state.mapCenterLatitude}
                mapCenterLongitude={this.state.mapCenterLongitude}
              />
              <BookingStatsPanel
                cohort={activeCohort}
              />
            </div>
          </div>
          { this.SimulationModal(
            this.state.showReportDialog,
            this.closeDialog
          ) }
        </div>
      </DocumentTitle>
    );
  }
}

function mapStateToProps(state) {
  const {
    activeCohortID,
  } = state.app;

  return {
    activeCohortID,
    activeCohort: state.cohorts[activeCohortID] || {},
    simulationGetRequest: state.requests.startSimulationRequest || {},
    simulationCompleted: state.app.simulationCompleted || false,
    totalBookings: state.app.totalBookings || 0,
    allocationRate: state.app.allocationRate || 0,
    successfulRides: state.app.successfulRides || 0,
    successRate: state.app.successRate || 0,
    driverIDs: state.app.driverIDs || [],
    passengerIDs: state.app.passengerIDs || [],
    errors: state.app.errors || {},
    timeoutEvent: state.app.timeoutEvent || false,
    bookings: state.app.bookings || {},
    startSimulationErrors: _.get(state.errors, 'startSimulation.errors', {}),
    cohortGetRequest: state.requests.cohortGetRequest || {},
    cohortGetErrors: _.get(state.errors, 'cohortGet.errors', []),
  };
}

export default connect(
  mapStateToProps, {
    startSimulation,
    cohortGet,
    unsetCohort,
    resetErrorState,
  }
)(SimulationPage);



/** WEBPACK FOOTER **
 ** ./src/js/views/autosim/SimulationPage.jsx
 **/